import React, { useMemo, useContext, Fragment, useRef, useCallback, useState } from 'react';
import ProductBox from '../ProductBox';
import CMNUI from '../CMNUI';
import { PatientChartContext } from '../../../contexts/patientChart';
import {
  PRODUCT_INFO, PATIENT, PRESCRIBER, INTAKE,
  SF_EMAIL_TEMPLATES, APPROVAL_VALIDATION_ERROR,
  PRESCRIPTION, KIT_EMAIL, ALLOWED_KEY_CODE,
  MODAL_KEY, CONFIRMATION_POPUP_KEY,
  APPROVAL_VALIDATION_FIELD as AVF,
} from '../../../constants/patientChart.constant';
import PPButton from '../../subComponents/button';
import Input from '../../subComponents/input';
import useValidationError from '../../../hooks/useValidationError';
import { pickSecretAutoCompleteHash } from '../../../utils/random';

export const RxUI = ({
  orderId = 0,
  patientId = 0,
  doctorId = 0,
  product = PRODUCT_INFO(),
  patient = PATIENT(),
  history = {},
  prescriber = PRESCRIBER(),
  intake = INTAKE(),
  kits = [{
    key: '',
    items: [PRESCRIPTION()],
    email: KIT_EMAIL(),
  }],
  medicalOrderNotes = {},
  formField = {},
  templates = SF_EMAIL_TEMPLATES(),
  validationError = APPROVAL_VALIDATION_ERROR(),

  getCmnPdfData = async (requestBody = {}) => {},
  setFormField = () => {},
}) => {
  const editorRef = useRef(new Map());
  const { setJoditEditorTemplateForKit } = useContext(PatientChartContext);

  const handleTemplateChange = useCallback(({ updatedState }) => {
    setJoditEditorTemplateForKit(updatedState);
  }, []);

  const { productBoxes, prescriberNpi} = useMemo(() => {
    let productBoxes = [<Fragment />];
    productBoxes = [];

    let prescriptions = [PRESCRIPTION()];
    prescriptions = [];

    let npis = new Set(['']);
    npis.delete('');

    let prescriberNpi = '';

    for (let i = 0; i < kits.length; i++) {
      const { key, items, email, kitPId } = kits[i];
      prescriptions.push(...items);
      productBoxes.push(
        <ProductBox
          key={key}
          kitKey={key}
          kitPId={kitPId}
          prescriptions={items}
          email={email}
          templates={templates}
          joditConfig={{
            id: key,
            name: key,
            className: key,
            editorRef: editorRef,
            value: '',
            extras: {
              key,
              kitPId,
            },
            onChange: handleTemplateChange,
          }}
          productCollection={product.productMap}
          validationError={validationError}
        />
      );
    }

    for (let i = 0; i < prescriptions.length; i++) {
      const { NPI } = prescriptions[i];
      npis.add(NPI);
    }

    if (npis.size === 1) {
      prescriberNpi = [...npis.values()].join(', ');
    }

    return { productBoxes, prescriberNpi };
  }, [kits, editorRef, handleTemplateChange, templates, product, validationError]);

  return (
    <div className='flex flex-column flex-fill m-b-5'>
      <CMNUI
        orderId={orderId}
        patientId={patientId}
        doctorId={doctorId}
        product={product}
        patient={patient}
        history={history}
        intake={intake}
        medicalOrderNotes={medicalOrderNotes}
        getCmnPdfData={getCmnPdfData}
        formField={formField}
        setFormField={setFormField}
      />
  
      <div className='flex align-items-center gap-8 m-b-3'>
        <div className='flex'>Patient Name:</div>
        <div className='flex bold'>{patient.FirstName} {patient.LastName},</div>
        <div className='flex'>Sex:</div>
        <div className='flex bold'>{patient.Gender},</div>
        <div className='flex'>DOB:</div>
        <div className='flex bold'>{patient.DOB},</div>
        <div className='flex'>City, State:</div>
        <div className='flex bold'>{patient.City}, {patient.State}</div>
      </div>
  
      <div className='flex align-items-center gap-8 m-b-5'>
        <div className='flex gap-8 width-half'>
          <div className='flex'>Prescriber:</div>
          <div className='flex'>{prescriber.Prefix} {prescriber.FirstName} {prescriber.LastName}</div>
        </div>
  
        <div className='flex gap-8 width-half'>
          <div className='flex'>NPI:</div>
          <div className='flex'>{prescriberNpi}</div>
        </div>
      </div>
  
      {productBoxes}
    </div>
  );
}

export const RxActions = ({
  validationError = APPROVAL_VALIDATION_ERROR(),

  onApprove: handleApprove = async () => {},
}) => {
  const {
    pin,
    setPin,
    loading,
    handleMultipleModalClose,
  } = useContext(PatientChartContext);

  const [readOnly, setReadOnly] = useState(true);

  const error = useValidationError({
    errorKey: `${AVF.pin}`,
    validationError,
  });

  const handlePinKeyDown = (event) => {
    if (!ALLOWED_KEY_CODE.has(event.keyCode)) {
      event.preventDefault();
    }
  };

  const handlePinChange = ({ target: { value } }) => {
    setPin(value);
  };

  const handleFocus = useCallback(() => {
    setReadOnly(false);
  }, []);

  const handleBlur = useCallback(() => {
    setReadOnly(true);
  }, []);

  const _disabled = useMemo(() => validationError.size > 0, [validationError]);
  const _loading = useMemo(() => loading.has(MODAL_KEY.APPROVE), [loading]);

  return (
    <div className='flex align-items-center justify-content-space-between'>
      <div className='approval-pin flex align-items-center width-180'>
        <p className='m-t-0 m-b-0 m-r-2'>4 DIGIT PIN</p>
        <Input
          name={`approval-pin-${pickSecretAutoCompleteHash(4)}-hash-${pickSecretAutoCompleteHash(4)}`}
          type='password'
          maxLength={4}
          autoComplete={`nope-approval-pin-${pickSecretAutoCompleteHash(4)}-hash-${pickSecretAutoCompleteHash(4)}`}
          value={pin}
          readOnly={readOnly}
          error={error.has}
          fontSize='16'
          className='width-80'
          inputClassName='tracking-widest align-center'
          onChange={handlePinChange}
          onKeyDown={handlePinKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </div>
  
      <div className='flex flex-column width-390 justify-content-center'>
        <PPButton label='Send Prescription(s) and Email(s)' variant='contained' loading={_loading} disabled={_disabled} onClick={handleApprove.bind(this, { key: CONFIRMATION_POPUP_KEY.APPROVE, modal: {} })} />
      </div>
  
      <div className='flex align-items-center justify-content-flex-end width-180'>
        <PPButton label='Cancel' variant='contained' disabled={_loading} onClick={handleMultipleModalClose.bind(this, { keys: [MODAL_KEY.APPROVE] })} />
      </div>
    </div>
  );
};

export default RxUI;
